import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { motion } from "framer-motion";
import { ChartBar, Trophy, CaretDown, X } from "@phosphor-icons/react";

const PERIODS = [
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "custom", label: "Custom" },
];

const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export default function ActivityReports() {
  const { user } = useAuth();
  const isMpOrAdmin = user?.role === "managing_partner" || user?.role === "admin";
  const isFdMpAdmin = isMpOrAdmin || user?.role === "field_director";
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("week");
  const [scope, setScope] = useState(user?.role === "managing_partner" ? "mine" : "firm");
  const [customStart, setCustomStart] = useState(daysAgoIso(30));
  const [customEnd, setCustomEnd] = useState(todayIso());
  const [selectableAgents, setSelectableAgents] = useState([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  const [agentPickerOpen, setAgentPickerOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [mpOptions, setMpOptions] = useState([]);
  const [selectedMpId, setSelectedMpId] = useState("");
  const [summary, setSummary] = useState(null);
  const [perAgent, setPerAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Build query string for the report endpoints.
  const reportQuery = useMemo(() => {
    const p = new URLSearchParams();
    p.set("period", period);
    if (scope) p.set("scope", scope);
    if (period === "custom") {
      p.set("start", customStart);
      p.set("end", customEnd);
    }
    if (selectedAgentIds.length > 0) p.set("agent_ids", selectedAgentIds.join(","));
    if (isAdmin && selectedMpId) p.set("mp_id", selectedMpId);
    return p.toString();
  }, [period, scope, customStart, customEnd, selectedAgentIds, isAdmin, selectedMpId]);

  // Validate custom dates before firing.
  const customDatesValid = period !== "custom" || (
    customStart && customEnd && new Date(customStart) <= new Date(customEnd)
  );

  const loadReports = useCallback(async () => {
    if (!customDatesValid) return;
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get(`/activity-reports?${reportQuery}`),
        isMpOrAdmin ? api.get(`/activity-reports/agents?${reportQuery}`) : Promise.resolve({ data: null }),
      ]);
      setSummary(s.data);
      setPerAgent(p.data);
    } finally {
      setLoading(false);
    }
  }, [reportQuery, customDatesValid, isMpOrAdmin]);

  useEffect(() => { loadReports(); }, [loadReports]);

  // Load the user pool for the agent multi-select (MP / FD / Admin only).
  const loadAgents = useCallback(async () => {
    if (!isFdMpAdmin) return;
    try {
      const { data } = await api.get("/users");
      const pool = (data || [])
        .filter((u) => ["agent", "field_director", "managing_partner"].includes(u.role) && u.id !== user.id)
        .map((u) => ({ id: u.id, name: u.name, role: u.role }));
      setSelectableAgents(pool);
      if (isAdmin) {
        setMpOptions(
          (data || [])
            .filter((u) => u.role === "managing_partner")
            .map((u) => ({ id: u.id, name: u.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      }
    } catch {
      setSelectableAgents([]);
      setMpOptions([]);
    }
  }, [isFdMpAdmin, isAdmin, user.id]);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const toggleAgent = (id) => {
    setSelectedAgentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const clearAgents = () => setSelectedAgentIds([]);

  const visibleAgents = useMemo(() => {
    const q = agentSearch.trim().toLowerCase();
    if (!q) return selectableAgents;
    return selectableAgents.filter((a) => a.name.toLowerCase().includes(q));
  }, [selectableAgents, agentSearch]);

  const maxPoints = Math.max(1, ...((summary?.metrics || []).map((m) => m.points)));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500">Activity</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-1 flex items-center gap-3">
            Reports <ChartBar weight="fill" className="text-[#2563eb]" />
          </h1>
          <p className="text-zinc-600 mt-2">{summary?.label || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isMpOrAdmin && (
            <div className="brut bg-white p-1.5 inline-flex" data-testid="reports-scope-toggle">
              <button onClick={() => setScope("mine")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "mine" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="reports-scope-mine">Mine</button>
              {user?.role === "managing_partner" && <button onClick={() => setScope("team")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "team" ? "bg-[#2563eb] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="reports-scope-team">My Team</button>}
              <button onClick={() => setScope("firm")} className={`px-3 py-2 font-bold uppercase text-xs tracking-widest ${scope === "firm" ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`} data-testid="reports-scope-firm">{user?.role === "admin" ? "Firm" : "All"}</button>
            </div>
          )}
          <div className="brut bg-white p-1.5 inline-flex" data-testid="reports-period-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={`px-4 py-2 font-bold uppercase text-xs tracking-widest ${period === p.key ? "bg-[#09090b] text-white" : "text-black hover:bg-zinc-100"}`}
                data-testid={`reports-period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Custom date range + agent multi-select row */}
      {(period === "custom" || isFdMpAdmin) && (
        <div className="brut bg-white p-4 flex flex-col md:flex-row md:items-end gap-4 flex-wrap" data-testid="reports-advanced-filters">
          {period === "custom" && (
            <>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Start</div>
                <input
                  type="date"
                  value={customStart}
                  max={customEnd || undefined}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
                  data-testid="reports-custom-start"
                />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">End</div>
                <input
                  type="date"
                  value={customEnd}
                  min={customStart || undefined}
                  max={todayIso()}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
                  data-testid="reports-custom-end"
                />
              </div>
              {!customDatesValid && (
                <div className="text-xs font-bold uppercase tracking-widest text-[#ef4444]">End must be on or after start</div>
              )}
            </>
          )}

          {isAdmin && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Managing Partner</div>
              <select
                value={selectedMpId}
                onChange={(e) => setSelectedMpId(e.target.value)}
                className="border-2 border-black h-10 px-3 font-medium bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#09090b] min-w-[200px]"
                data-testid="reports-mp-filter"
              >
                <option value="">All Managing Partners</option>
                {mpOptions.map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
          )}

          {isFdMpAdmin && (
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1">Filter by Agent(s)</div>
              <button
                type="button"
                onClick={() => setAgentPickerOpen((o) => !o)}
                className="border-2 border-black h-10 px-3 font-medium bg-white inline-flex items-center gap-2 min-w-[220px] justify-between focus:outline-none focus:shadow-[3px_3px_0_0_#09090b]"
                data-testid="reports-agent-picker-btn"
              >
                <span className="text-sm">
                  {selectedAgentIds.length === 0
                    ? "All in scope"
                    : `${selectedAgentIds.length} selected`}
                </span>
                <CaretDown size={14} weight="bold" />
              </button>

              {selectedAgentIds.length > 0 && (
                <button
                  type="button"
                  onClick={clearAgents}
                  className="ml-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-[#ef4444]"
                  data-testid="reports-agent-clear"
                >
                  <X size={12} weight="bold" /> Clear
                </button>
              )}

              {agentPickerOpen && (
                <div
                  className="absolute z-30 top-[68px] left-0 brut bg-white p-3 w-[320px] max-h-[360px] overflow-auto shadow-[6px_6px_0_0_#09090b]"
                  data-testid="reports-agent-picker"
                >
                  <input
                    type="text"
                    placeholder="Search…"
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    className="w-full border-2 border-black h-9 px-2 text-sm mb-2 focus:outline-none"
                    data-testid="reports-agent-search"
                  />
                  {visibleAgents.length === 0 && (
                    <div className="text-xs text-zinc-500 py-3 text-center">No matches.</div>
                  )}
                  {visibleAgents.map((a) => {
                    const checked = selectedAgentIds.includes(a.id);
                    return (
                      <label
                        key={a.id}
                        className="flex items-center gap-2 py-1.5 px-1 cursor-pointer hover:bg-[#f4f4f5]"
                        data-testid={`reports-agent-opt-${a.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAgent(a.id)}
                          className="w-4 h-4 accent-[#2563eb]"
                        />
                        <span className="font-medium text-sm">{a.name}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-zinc-400">
                          {a.role === "managing_partner" ? "MP" : a.role === "field_director" ? "FD" : "Agent"}
                        </span>
                      </label>
                    );
                  })}
                  <div className="mt-2 pt-2 border-t-2 border-dashed border-zinc-300 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setAgentPickerOpen(false)}
                      className="border-2 border-black px-3 h-8 text-xs font-bold uppercase tracking-widest hover:bg-[#09090b] hover:text-white"
                      data-testid="reports-agent-done"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top metric: total points for the period */}
      <div className="brut p-6 bg-[#09090b] text-white flex items-center justify-between" data-testid="reports-total">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-400">Total Points</div>
          <div className="font-display font-black text-5xl tracking-tighter mt-1 text-white">{summary?.total_points ?? 0}</div>
        </div>
        <Trophy size={48} weight="fill" className="text-[#2563eb]" />
      </div>

      {/* Per-metric grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(summary?.metrics || []).map((m, i) => {
          const widthPct = Math.min(100, (m.points / maxPoints) * 100);
          return (
            <motion.div
              key={m.key}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
              className="brut p-5 bg-white"
              data-testid={`metric-row-${m.key}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{m.label}</div>
                  <div className="font-display font-black text-3xl tracking-tighter mt-1">
                    {m.kind === "premium"
                      ? `${fmt$(m.amount)} · ${m.cases} ${m.cases === 1 ? "case" : "cases"}`
                      : m.count}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">Base: {String(m.base_points)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Points</div>
                  <div className="font-display font-black text-2xl tracking-tighter">{m.points}</div>
                </div>
              </div>
              <div className="mt-3 h-2 border-2 border-black bg-white overflow-hidden">
                <div className="h-full bg-[#2563eb]" style={{ width: `${widthPct}%` }} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Appointments by source — dedicated drill-down for Set + Seen */}
      {summary?.appts_by_source && summary.appts_by_source.some((m) => m.total > 0) && (
        <div className="brut bg-white p-5" data-testid="reports-appts-by-source">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Appointments</div>
          <div className="font-display font-black text-2xl tracking-tight">By Source · Set &amp; Seen</div>
          <p className="text-xs text-zinc-600 mt-1">Where every booked and attended appointment originated from.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {summary.appts_by_source.map((row) => (
              <div key={row.key} className="border-2 border-black p-4 bg-white" data-testid={`appts-src-${row.key}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <div className="font-display font-black text-lg tracking-tight">{row.label}</div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">Total</div>
                    <div className="font-display font-black text-2xl tracking-tighter">{row.total}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {row.counts.filter((s) => s.source !== "unspecified" || s.count > 0).map((s) => {
                    const pct = row.total > 0 ? Math.round((s.count / row.total) * 100) : 0;
                    const bg =
                      s.source === "natural_market" ? "#a7f3d0" :
                      s.source === "referral" ? "#fde68a" :
                      s.source === "paid_lead" ? "#bae6fd" :
                      s.source === "cold_lead" ? "#c7d2fe" :
                      s.source === "networking" ? "#fed7aa" :
                      "#e4e4e7";
                    return (
                      <div key={s.source} className="border-2 border-black p-2" data-testid={`appts-src-${row.key}-${s.source}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="inline-block border-2 border-black px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest" style={{ background: bg }}>
                            {s.label}
                          </span>
                          <span className="font-display font-black text-base tracking-tight">{s.count}</span>
                        </div>
                        <div className="mt-1 h-1.5 border-2 border-black bg-white overflow-hidden">
                          <div className="h-full" style={{ width: `${pct}%`, background: bg }} />
                        </div>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500 mt-0.5">{pct}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-source breakdown */}
      {summary?.by_source && summary.by_source.length > 0 && (
        <div className="brut bg-white p-5" data-testid="reports-by-source">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">By Source</div>
          <div className="font-display font-black text-2xl tracking-tight">Where Your Activity Came From</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            {summary.by_source.map((s) => (
              <div key={s.source} className="border-2 border-black p-4 bg-white" data-testid={`source-tile-${s.source}`}>
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="inline-block border-2 border-black px-2 py-0.5 text-[10px] font-black uppercase tracking-widest"
                    style={{
                      background:
                        s.source === "natural_market" ? "#a7f3d0" :
                        s.source === "referral" ? "#fde68a" :
                        s.source === "paid_lead" ? "#bae6fd" :
                        s.source === "cold_lead" ? "#c7d2fe" :
                        s.source === "networking" ? "#fed7aa" :
                        "#e4e4e7",
                    }}
                  >
                    {s.label}
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{s.count} entries</span>
                </div>
                {s.amount > 0 && (
                  <div className="text-xs text-zinc-500">Premium <span className="font-display font-black text-black">{fmt$(s.amount)}</span></div>
                )}
                <div className="text-xs text-zinc-500 mt-0.5">Points <span className="font-display font-black text-black">{s.points}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per-agent breakdown (MP/Admin only) */}
      {isMpOrAdmin && perAgent?.rows && perAgent.rows.length > 0 && (
        <div className="brut bg-white overflow-x-auto">
          <div className="px-5 pt-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">By Agent</div>
            <div className="font-display font-black text-2xl tracking-tight">Team Breakdown</div>
          </div>
          <table className="w-full border-collapse mt-3" data-testid="per-agent-table">
            <thead>
              <tr className="bg-[#09090b] text-white">
                <th className="text-left px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Agent</th>
                {(perAgent.metrics || []).map((m) => (
                  <th key={m.key} className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">{m.label}</th>
                ))}
                <th className="text-right px-3 py-3 font-bold uppercase text-[11px] tracking-widest">Points</th>
              </tr>
            </thead>
            <tbody>
              {perAgent.rows.map((r) => (
                <tr key={r.agent_id} className="border-b-2 border-black last:border-b-0 hover:bg-[#f4f4f5]">
                  <td className="px-3 py-2 font-bold">{r.agent_name}</td>
                  {(perAgent.metrics || []).map((m) => {
                    const v = r[m.key] || 0;
                    return (
                      <td key={m.key} className="px-3 py-2 text-right">
                        {m.kind === "premium" ? fmt$(v) : v}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-display font-black">{r.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && customDatesValid && (summary?.total_points ?? 0) === 0 && (
        <div className="brut p-8 bg-white text-center text-zinc-500" data-testid="reports-empty">
          No activity logged for this {period === "custom" ? "range" : period}. Head to <span className="font-bold">Log Activity</span> to start the streak.
        </div>
      )}
    </div>
  );
}
